// V4.2.3: Loading Components - Skeleton screens ve loading indicators
import React from 'react';
import { styles } from '../constants/styles';

/**
 * Genel loading spinner
 */
export const LoadingSpinner = ({ size = 40, color = '#284D3A' }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
      <div
        style={{
          width: size,
          height: size,
          border: `4px solid ${color}20`,
          borderTop: `4px solid ${color}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}
      >
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

/**
 * Kumbara listesi için skeleton
 */
export const KumbaraListSkeleton = ({ count = 5 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '15px',
            background: '#f7fafc',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  height: '12px',
                  width: '80px',
                  background: '#e2e8f0',
                  borderRadius: '4px',
                  marginBottom: '8px'
                }}
              />
              <div
                style={{
                  height: '10px',
                  width: '60px',
                  background: '#e2e8f0',
                  borderRadius: '4px',
                  marginBottom: '8px'
                }}
              />
              <div
                style={{
                  height: '16px',
                  width: '200px',
                  background: '#e2e8f0',
                  borderRadius: '4px',
                  marginBottom: '5px'
                }}
              />
              <div
                style={{
                  height: '14px',
                  width: '150px',
                  background: '#e2e8f0',
                  borderRadius: '4px',
                  marginBottom: '8px'
                }}
              />
              <div
                style={{
                  height: '24px',
                  width: '80px',
                  background: '#e2e8f0',
                  borderRadius: '4px',
                  display: 'inline-block'
                }}
              />
            </div>
            <div
              style={{
                height: '36px',
                width: '80px',
                background: '#e2e8f0',
                borderRadius: '8px'
              }}
            />
          </div>
        </div>
      ))}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
};

/**
 * Tablo için skeleton
 */
export const TableSkeleton = ({ rows = 5, cols = 4 }) => {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ ...styles.table, width: '100%' }}>
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, index) => (
              <th key={index} style={styles.th}>
                <div
                  style={{
                    height: '16px',
                    width: '100px',
                    background: '#e2e8f0',
                    borderRadius: '4px',
                    margin: '0 auto',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: cols }).map((_, colIndex) => (
                <td key={colIndex} style={styles.td}>
                  <div
                    style={{
                      height: '14px',
                      width: '80%',
                      background: '#e2e8f0',
                      borderRadius: '4px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: `${rowIndex * 0.1}s`
                    }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
};

/**
 * Kart için skeleton
 */
export const CardSkeleton = ({ count = 3 }) => {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '20px',
            background: '#f7fafc',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}
        >
          <div
            style={{
              height: '20px',
              width: '60%',
              background: '#e2e8f0',
              borderRadius: '4px',
              marginBottom: '15px'
            }}
          />
          <div
            style={{
              height: '32px',
              width: '80%',
              background: '#e2e8f0',
              borderRadius: '4px',
              marginBottom: '10px'
            }}
          />
          <div
            style={{
              height: '14px',
              width: '100%',
              background: '#e2e8f0',
              borderRadius: '4px',
              marginBottom: '8px'
            }}
          />
          <div
            style={{
              height: '14px',
              width: '70%',
              background: '#e2e8f0',
              borderRadius: '4px'
            }}
          />
        </div>
      ))}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
};

/**
 * Button loading state
 */
export const ButtonLoading = ({ children, loading, ...props }) => {
  return (
    <button
      {...props}
      disabled={loading || props.disabled}
      style={{
        ...props.style,
        opacity: loading ? 0.6 : 1,
        cursor: loading ? 'not-allowed' : 'pointer',
        position: 'relative'
      }}
    >
      {loading ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <LoadingSpinner size={16} color="currentColor" />
          <span>Yükleniyor...</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
};

/**
 * Full page loading
 */
export const FullPageLoading = ({ message = 'Yükleniyor...' }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}
    >
      <LoadingSpinner size={60} />
      <p style={{ marginTop: '20px', fontSize: '16px', color: '#4a5568' }}>{message}</p>
    </div>
  );
};

export default LoadingSpinner;

